/**
 * Model catalog — static fallback + live provider API fetching.
 *
 * Provides `fetchProviderModels(provider, apiKey)` for live model lists and
 * `getStaticModels(provider)` as an offline fallback.
 * OMP model listing is handled by `OmpDriver.listModels()` in the bridge driver.
 */

import { PROVIDER_ENDPOINTS, resolveProviderModelsUrl } from "./provider-endpoints.js";

/**
 * A single LLM model entry returned by the model catalog.
 *
 * Used by `getModels`, `getStaticModels`, and `fetchProviderModels` to represent
 * an available model for a provider.
 *
 * @docLink packages/bridge/api-reference#models
 */
export interface ModelEntry {
  /** Provider-specific model identifier used in API calls (e.g. `"claude-sonnet-4-5"`). */
  id: string;
  /** Human-readable display name for UIs and logs (e.g. `"Claude Sonnet 4.5"`). */
  name: string;
}

// ---------------------------------------------------------------------------
// Provider API configs (for fetching model lists)
// ---------------------------------------------------------------------------

/** Extract ModelEntry[] from a provider's parsed models-list JSON response. */
type ParseModels = (body: any) => ModelEntry[];

/** Humanize a model ID into a display name (e.g. "claude-sonnet-4-6" → "Claude Sonnet 4 6") */
function humanize(id: string): string {
  return id
    .replace(/^accounts\/[^/]+\/models\//, "")
    .replace(/[-_]/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Filter function used by providers that return the OpenAI-style /v1/models list.
 * Keeps only chat/completion models, skips embeddings, moderation, tts, whisper, dall-e, etc.
 */
function isLikelyChatModel(id: string): boolean {
  const skip = /embed|moderat|tts|whisper|dall-e|davinci|babbage|ada-00|text-/i;
  return !skip.test(id);
}

// Per-provider response parsers. URL / headers / queryParam live in the shared
// PROVIDER_ENDPOINTS map (provider-endpoints.ts), merged in fetchProviderModels.
const PROVIDER_PARSERS: Record<string, ParseModels> = {
  anthropic: (body) => {
    const models = body?.data ?? [];
    return models
      .filter((m: any) => m.id && !m.id.includes("embed"))
      .map((m: any) => ({
        id: m.id,
        name: m.display_name ?? humanize(m.id),
      }));
  },
  openai: (body) => {
    const models = body?.data ?? [];
    return models
      .filter((m: any) => m.id && isLikelyChatModel(m.id))
      .map((m: any) => ({ id: m.id, name: humanize(m.id) }))
      .sort((a: ModelEntry, b: ModelEntry) => a.id.localeCompare(b.id));
  },
  openrouter: (body) => {
    const models = body?.data ?? [];
    return models
      .filter((m: any) => m.id)
      .map((m: any) => ({ id: m.id, name: m.name ?? humanize(m.id) }))
      .sort((a: ModelEntry, b: ModelEntry) => a.id.localeCompare(b.id));
  },
  mistral: (body) => {
    const models = body?.data ?? [];
    return models
      .filter((m: any) => m.id && isLikelyChatModel(m.id))
      .map((m: any) => ({ id: m.id, name: humanize(m.id) }))
      .sort((a: ModelEntry, b: ModelEntry) => a.id.localeCompare(b.id));
  },
  groq: (body) => {
    const models = body?.data ?? [];
    return models
      .filter((m: any) => m.id && isLikelyChatModel(m.id))
      .map((m: any) => ({ id: m.id, name: humanize(m.id) }))
      .sort((a: ModelEntry, b: ModelEntry) => a.id.localeCompare(b.id));
  },
  deepseek: (body) => {
    const models = body?.data ?? [];
    return models
      .map((m: any) => ({ id: m.id, name: humanize(m.id) }))
      .sort((a: ModelEntry, b: ModelEntry) => a.id.localeCompare(b.id));
  },
  xai: (body) => {
    const models = body?.data ?? [];
    return models
      .filter((m: any) => m.id && isLikelyChatModel(m.id))
      .map((m: any) => ({ id: m.id, name: humanize(m.id) }))
      .sort((a: ModelEntry, b: ModelEntry) => a.id.localeCompare(b.id));
  },
  together: (body) => {
    const models = body?.data ?? body ?? [];
    const arr = Array.isArray(models) ? models : [];
    return arr
      .filter((m: any) => m.id && m.type === "chat")
      .map((m: any) => ({ id: m.id, name: m.display_name ?? humanize(m.id) }))
      .sort((a: ModelEntry, b: ModelEntry) => a.id.localeCompare(b.id));
  },
  fireworks: (body) => {
    const models = body?.data ?? [];
    return models
      .map((m: any) => ({ id: m.id, name: humanize(m.id) }))
      .sort((a: ModelEntry, b: ModelEntry) => a.id.localeCompare(b.id));
  },
  google: (body) => {
    const models = body?.models ?? [];
    return models
      .filter((m: any) => m.supportedGenerationMethods?.includes("generateContent"))
      .map((m: any) => ({
        id: (m.name ?? "").replace(/^models\//, ""),
        name: m.displayName ?? humanize(m.name ?? ""),
      }))
      .sort((a: ModelEntry, b: ModelEntry) => a.id.localeCompare(b.id));
  },
};

// ---------------------------------------------------------------------------
// Live fetch
// ---------------------------------------------------------------------------

/**
 * Result of a model-list fetch operation, with source attribution.
 *
 * Returned by {@link fetchProviderModels} and {@link getModels}. When `ok` is `false`,
 * `models` is empty and `error` describes the failure. The `source` field indicates
 * whether the list came from a live API call or the offline static catalog.
 *
 * @docLink packages/bridge/api-reference#models
 */
export interface FetchModelsResult {
  /** `true` when the fetch succeeded and `models` is populated. */
  ok: boolean;
  /** Fetched model list; empty on failure. */
  models: ModelEntry[];
  /** Human-readable error description when `ok` is `false`. */
  error?: string;
  /** Whether the result came from a live API call or the static fallback. */
  source: "api" | "static";
}

/**
 * Options controlling where a model-list fetch is addressed.
 *
 * @since 1.5.0
 */
export interface FetchModelsOptions {
  /**
   * Base URL to resolve the provider's models endpoint against — e.g. the
   * gateway a session already routes completions through. Overrides the
   * provider's `baseUrlEnv` value.
   */
  baseUrl?: string;
  /**
   * Environment consulted for the provider's base-URL env var
   * (`ANTHROPIC_BASE_URL` for `anthropic`). Defaults to `process.env`; pass an
   * explicit record to keep a call hermetic.
   */
  env?: Record<string, string | undefined>;
}

/**
 * Fetch the model list from a provider's REST API.
 *
 * Returns `{ ok: true, models, source: 'api' }` on success, or
 * `{ ok: false, models: [], error }` on any HTTP or network failure — the
 * caller should fall back to {@link getStaticModels} in that case.
 *
 * When the provider is reachable through a gateway, the request targets the
 * configured base URL rather than the provider's public API — see
 * {@link resolveProviderModelsUrl}. Without one, behaviour is unchanged.
 *
 * @param provider - Provider name matching a key in the shared `PROVIDER_ENDPOINTS` map
 *   (e.g. `"anthropic"`, `"openai"`, `"google"`).
 * @param apiKey - API key forwarded in the request header or query parameter.
 * @param options - Optional base-URL / environment overrides.
 * @returns Resolved model list with source attribution.
 * @docLink packages/bridge/api-reference#models
 */
export async function fetchProviderModels(
  provider: string,
  apiKey: string,
  options: FetchModelsOptions = {},
): Promise<FetchModelsResult> {
  const endpoint = PROVIDER_ENDPOINTS[provider];
  const parse = PROVIDER_PARSERS[provider];
  if (!endpoint || !parse) {
    return {
      ok: false,
      models: [],
      error: `No API config for provider: ${provider}`,
      source: "static",
    };
  }

  let url =
    resolveProviderModelsUrl(provider, {
      baseUrl: options.baseUrl,
      env: options.env ?? process.env,
    }) ?? endpoint.url;
  if (endpoint.queryParam) {
    url += `?${endpoint.queryParam}=${encodeURIComponent(apiKey)}`;
  }

  try {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 10_000);

    const response = await fetch(url, {
      method: "GET",
      headers: endpoint.headers(apiKey),
      signal: controller.signal,
    });

    clearTimeout(timeout);

    if (!response.ok) {
      return {
        ok: false,
        models: [],
        error: `API returned HTTP ${response.status}`,
        source: "static",
      };
    }

    const body = await response.json();
    const models = parse(body);

    return { ok: true, models, source: "api" };
  } catch (err: any) {
    const msg =
      err.name === "AbortError" ? "Request timed out (10s)" : (err.message ?? "Network error");
    return { ok: false, models: [], error: msg, source: "static" };
  }
}

/**
 * Get models for a provider, trying the live API first and falling back to the static catalog.
 *
 * When no `apiKey` is provided the static catalog is returned immediately without
 * a network call. When the live API fails or returns an empty list, the static
 * catalog is used as a fallback — the result's `source` field indicates which was used.
 *
 * @param provider - Provider name (e.g. `"anthropic"`, `"openai"`, `"google"`).
 * @param apiKey - Optional API key; omit to skip the live fetch and use the static catalog.
 * @param options - Optional base-URL / environment overrides (see {@link FetchModelsOptions}).
 * @returns Resolved model list with source attribution.
 * @docLink packages/bridge/api-reference#models
 */
export async function getModels(
  provider: string,
  apiKey?: string,
  options: FetchModelsOptions = {},
): Promise<FetchModelsResult> {
  if (!apiKey) {
    const models = getStaticModels(provider);
    return { ok: true, models, source: "static" };
  }

  const result = await fetchProviderModels(provider, apiKey, options);
  if (result.ok && result.models.length > 0) {
    return result;
  }

  // Fall back to static
  const staticModels = getStaticModels(provider);
  if (staticModels.length > 0) {
    return { ok: true, models: staticModels, source: "static", error: result.error };
  }

  return result;
}

// ---------------------------------------------------------------------------
// Static catalog (offline fallback)
// ---------------------------------------------------------------------------

/**
 * Offline model catalog keyed by provider name.
 *
 * Used as a fallback by {@link getModels} when the live provider API is unreachable or
 * returns an empty list. Covers Anthropic, OpenAI, Google, Mistral, Groq, DeepSeek,
 * xAI, Together, Fireworks, and OpenRouter. Update this list when providers add new
 * models and a live fetch is not available.
 *
 * @docLink packages/bridge/api-reference#models
 */
export const STATIC_MODELS: Record<string, ModelEntry[]> = {
  anthropic: [
    { id: "claude-opus-4-6", name: "Claude Opus 4.6" },
    { id: "claude-sonnet-4-6", name: "Claude Sonnet 4.6" },
    { id: "claude-haiku-4-5-20251001", name: "Claude Haiku 4.5" },
    { id: "claude-opus-4-5", name: "Claude Opus 4.5" },
    { id: "claude-sonnet-4-5", name: "Claude Sonnet 4.5" },
  ],
  openai: [
    { id: "gpt-4o", name: "GPT-4o" },
    { id: "gpt-4o-mini", name: "GPT-4o Mini" },
    { id: "gpt-4.1", name: "GPT-4.1" },
    { id: "gpt-4.1-mini", name: "GPT-4.1 Mini" },
    { id: "o3", name: "o3" },
    { id: "o4-mini", name: "o4-mini" },
  ],
  google: [
    { id: "gemini-2.5-pro", name: "Gemini 2.5 Pro" },
    { id: "gemini-2.5-flash", name: "Gemini 2.5 Flash" },
    { id: "gemini-2.0-flash", name: "Gemini 2.0 Flash" },
  ],
  mistral: [
    { id: "mistral-large-latest", name: "Mistral Large" },
    { id: "mistral-small-latest", name: "Mistral Small" },
    { id: "codestral-latest", name: "Codestral" },
  ],
  groq: [
    { id: "llama-3.3-70b-versatile", name: "Llama 3.3 70B" },
    { id: "llama-3.1-8b-instant", name: "Llama 3.1 8B" },
    { id: "mixtral-8x7b-32768", name: "Mixtral 8x7B" },
  ],
  deepseek: [
    { id: "deepseek-chat", name: "DeepSeek Chat" },
    { id: "deepseek-reasoner", name: "DeepSeek Reasoner" },
  ],
  xai: [
    { id: "grok-3", name: "Grok 3" },
    { id: "grok-3-mini", name: "Grok 3 Mini" },
  ],
  together: [
    { id: "meta-llama/Llama-3.3-70B-Instruct-Turbo", name: "Llama 3.3 70B Turbo" },
    { id: "mistralai/Mixtral-8x7B-Instruct-v0.1", name: "Mixtral 8x7B" },
  ],
  fireworks: [
    { id: "accounts/fireworks/models/llama-v3p3-70b-instruct", name: "Llama 3.3 70B" },
    { id: "accounts/fireworks/models/deepseek-v3", name: "DeepSeek v3" },
  ],
  openrouter: [
    { id: "anthropic/claude-sonnet-4.6", name: "Claude Sonnet 4.6" },
    { id: "anthropic/claude-opus-4.6", name: "Claude Opus 4.6" },
    { id: "anthropic/claude-haiku-4.5", name: "Claude Haiku 4.5" },
    { id: "anthropic/claude-sonnet-4.5", name: "Claude Sonnet 4.5" },
    { id: "anthropic/claude-3.7-sonnet", name: "Claude 3.7 Sonnet" },
    { id: "openai/gpt-4o", name: "GPT-4o" },
    { id: "openai/gpt-4.1", name: "GPT-4.1" },
    { id: "openai/gpt-4.1-mini", name: "GPT-4.1 Mini" },
    { id: "google/gemini-2.5-pro", name: "Gemini 2.5 Pro" },
    { id: "google/gemini-2.5-flash", name: "Gemini 2.5 Flash" },
    { id: "meta-llama/llama-4-maverick", name: "Llama 4 Maverick" },
    { id: "meta-llama/llama-3.3-70b-instruct", name: "Llama 3.3 70B" },
    { id: "deepseek/deepseek-chat-v3-0324", name: "DeepSeek v3" },
    { id: "deepseek/deepseek-r1", name: "DeepSeek R1" },
    { id: "mistralai/mistral-large-2411", name: "Mistral Large 2" },
  ],
};

/**
 * Get models for a specific provider from the static catalog, or empty array if unknown.
 *
 * Pure synchronous lookup — no network call. Use when the provider API is unavailable
 * or when a fast offline fallback is needed (e.g. in the UI before credentials are configured).
 *
 * @param provider - Provider name matching a key in {@link STATIC_MODELS}.
 * @returns Offline model list, or `[]` for unknown providers.
 * @docLink packages/bridge/api-reference#models
 */
export function getStaticModels(provider: string): ModelEntry[] {
  return STATIC_MODELS[provider] ?? [];
}

// ---------------------------------------------------------------------------
// Model tier resolution (Flow v2 `run.model`)
// ---------------------------------------------------------------------------

/** Per-tier concrete model ids for one provider. */
export interface ModelTierOverrides {
  small?: string;
  default?: string;
  deep?: string;
}

/**
 * Built-in tier→model mapping per provider. This is the point of the closed
 * `small | default | deep` vocabulary: an authored flow names an intent and the
 * runtime owns which concrete model serves it, so flows stay portable across
 * model-lineup changes.
 *
 * These are **direct provider-API ids**. A gateway transport (Bedrock, Vertex,
 * Azure) addresses the same models by different ids — Bedrock prefixes
 * `us.anthropic.`, for instance — and that translation belongs to whoever owns
 * the deployment, not here. {@link resolveModelTier} therefore consults this
 * table only on the direct API and demands an explicit override otherwise,
 * rather than handing a gateway an id it cannot serve.
 *
 * Every id here must also appear in {@link STATIC_MODELS} — a test asserts it,
 * so the two cannot drift into naming a model the catalog disowns. That check
 * proves internal consistency only; it cannot tell you an id is still current
 * with a provider's live lineup.
 *
 * Where a provider's lineup has no genuinely distinct model for a tier, the
 * entry deliberately repeats the neighbouring one. That is a reviewable
 * property of that lineup, unlike inheriting whatever model the session
 * happened to start with — which is how `small` could silently mean Opus.
 */
export const PROVIDER_MODEL_TIERS: Record<string, Required<ModelTierOverrides>> = {
  anthropic: {
    small: "claude-haiku-4-5-20251001",
    default: "claude-sonnet-4-6",
    deep: "claude-opus-4-6",
  },
  openai: { small: "gpt-4o-mini", default: "gpt-4.1", deep: "o3" },
  google: { small: "gemini-2.0-flash", default: "gemini-2.5-flash", deep: "gemini-2.5-pro" },
  mistral: {
    small: "mistral-small-latest",
    default: "mistral-large-latest",
    deep: "mistral-large-latest",
  },
  groq: {
    small: "llama-3.1-8b-instant",
    default: "llama-3.3-70b-versatile",
    deep: "llama-3.3-70b-versatile",
  },
  deepseek: { small: "deepseek-chat", default: "deepseek-chat", deep: "deepseek-reasoner" },
  xai: { small: "grok-3-mini", default: "grok-3", deep: "grok-3" },
  together: {
    small: "mistralai/Mixtral-8x7B-Instruct-v0.1",
    default: "meta-llama/Llama-3.3-70B-Instruct-Turbo",
    deep: "meta-llama/Llama-3.3-70B-Instruct-Turbo",
  },
  fireworks: {
    small: "accounts/fireworks/models/llama-v3p3-70b-instruct",
    default: "accounts/fireworks/models/llama-v3p3-70b-instruct",
    deep: "accounts/fireworks/models/deepseek-v3",
  },
  openrouter: {
    small: "anthropic/claude-haiku-4.5",
    default: "anthropic/claude-sonnet-4.6",
    deep: "anthropic/claude-opus-4.6",
  },
};

/** Raised when a tier names an intent this provider cannot serve. */
export class ModelTierResolutionError extends Error {
  constructor(
    readonly tier: "small" | "deep",
    readonly provider: string | undefined,
  ) {
    super(
      `cannot resolve model tier "${tier}" for provider "${provider ?? "(none)"}": no built-in mapping and no configured override. ` +
        `Configure a "${tier}" model override for this provider, or author the node as model: "default" to inherit the session model.`,
    );
    this.name = "ModelTierResolutionError";
  }
}

/**
 * Resolve a Flow v2 `run.model` tier to a concrete model id.
 *
 * `"default"` and an absent tier **inherit `sessionModel` first**. The session
 * model is the one id known to work for this deployment — it already carries
 * whatever provider, lineup and gateway translation the host configured — so
 * preferring a built-in over it would silently downgrade a configured model
 * and, on a gateway transport, substitute an id that provider cannot serve.
 *
 * `"small"` and `"deep"` never fall back to `sessionModel`: they resolve from
 * an override, then from {@link PROVIDER_MODEL_TIERS} on the direct provider
 * API, and otherwise throw {@link ModelTierResolutionError}. Inheriting there
 * would run an arbitrary model behind an author's explicit request for a cheap
 * or a strong one — a cost or quality surprise that shows up only in the bill
 * or the output. Failing names it at the node instead.
 *
 * Pure: no I/O, no environment reads.
 */
export function resolveModelTier(
  tier: "small" | "default" | "deep" | undefined,
  sessionModel: string | undefined,
  options: { provider?: string; cloud?: string; overrides?: ModelTierOverrides } = {},
): string | undefined {
  // Built-in ids address the provider's own API; a gateway renames them.
  const directApi = !options.cloud || options.cloud === "default";
  const builtIn =
    directApi && options.provider ? PROVIDER_MODEL_TIERS[options.provider] : undefined;
  if (tier === "small" || tier === "deep") {
    const resolved = options.overrides?.[tier] ?? builtIn?.[tier];
    if (!resolved) throw new ModelTierResolutionError(tier, options.provider);
    return resolved;
  }
  return options.overrides?.default ?? sessionModel ?? builtIn?.default;
}
